import { FullPageLoader } from '@/components/loaders';
import { setOrgSlug } from '@/lib/api';
import { posthog } from '@/lib/posthog';
import { useTRPC, type RouterOutput } from '@/lib/trpc';
import { useAuth0 } from '@auth0/auth0-react';
import { TENANT_GROUP_TYPE } from '@beacon/analytics-events';

type MeResponse = RouterOutput['user']['me'];
type OrgMembership = MeResponse['memberships'][number];
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

interface OrgContextValue {
  me: MeResponse | null;
  currentOrg: OrgMembership | null;
  memberships: OrgMembership[];
  isLoading: boolean;
  error: string | null;
  switchOrg: (tenantSlug: string) => void;
}

const OrgContext = createContext<OrgContextValue>({
  me: null,
  currentOrg: null,
  memberships: [],
  isLoading: true,
  error: null,
  switchOrg: () => {},
});

export function useOrg() {
  return useContext(OrgContext);
}

const STORAGE_KEY = 'beacon:current-org-slug';

export function OrgProvider({ children }: { children: ReactNode }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedSlug, setSelectedSlug] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

  const { data: me, isLoading, error } = useQuery(trpc.user.me.queryOptions());

  // Set initial org slug when me data loads
  useEffect(() => {
    if (!me) return;
    const memberships = me.memberships ?? [];
    const saved = localStorage.getItem(STORAGE_KEY);
    const match = saved ? memberships.find((m) => m.tenantSlug === saved) : null;
    const org = match ?? memberships[0] ?? null;

    if (org) {
      setSelectedSlug(org.tenantSlug);
      setOrgSlug(org.tenantSlug);
      localStorage.setItem(STORAGE_KEY, org.tenantSlug);
    }
  }, [me]);

  const memberships = me?.memberships ?? [];
  const currentOrg = memberships.find((m) => m.tenantSlug === selectedSlug) ?? memberships[0] ?? null;

  // make sure PostHog is aware of the current organization and user's role within it
  useEffect(() => {
    if (!currentOrg) return;

    posthog.group(TENANT_GROUP_TYPE, currentOrg.tenantId, {
      name: currentOrg.tenantName,
      slug: currentOrg.tenantSlug,
    });
    posthog.setPersonProperties({ role: currentOrg.role });
  }, [currentOrg]);

  const switchOrg = useCallback(
    (tenantSlug: string) => {
      const org = memberships.find((m) => m.tenantSlug === tenantSlug);

      if (!org || org.tenantSlug === selectedSlug) return;
      setSelectedSlug(tenantSlug);
      setOrgSlug(tenantSlug);
      localStorage.setItem(STORAGE_KEY, tenantSlug);

      // Invalidate all queries so they refetch with the new org
      queryClient.invalidateQueries();

      navigate('/');
    },
    [memberships, queryClient, navigate, selectedSlug],
  );

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (error) {
    const isForbidden = error.message?.includes('FORBIDDEN') || error.message?.includes('Not a member');

    if (isForbidden) {
      return <ForbiddenScreen />;
    }

    return <p className="text-ds-orange-11 p-8 text-sm">Failed to load organization: {error.message}</p>;
  }

  if (!currentOrg) {
    return <p className="text-ds-text-secondary p-8 text-sm">No organization found. Please contact your admin.</p>;
  }

  return (
    <OrgContext.Provider
      value={{
        me: me ?? null,
        currentOrg,
        memberships,
        isLoading,
        error: null,
        switchOrg,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
}

function ForbiddenScreen() {
  const { logout } = useAuth0();

  return (
    <div className="bg-ds-surface-2 flex min-h-screen items-center justify-center">
      <div className="bg-ds-surface-foundation border-ds-stroke-primary w-full max-w-md rounded-xl border p-8 text-center shadow-sm">
        <h2 className="text-ds-text-primary text-lg font-semibold">Access Denied</h2>
        <p className="text-ds-text-secondary mt-3 text-sm">
          You don't have access to this application. Check your email for an invitation link.
        </p>
        <button
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className="border-ds-stroke-primary text-ds-text-primary hover:bg-ds-surface-2 mt-6 rounded-lg border px-5 py-2 text-sm transition"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
